import { useEffect, useState } from 'react';
import { useStore } from '../../store/useStore';

export const PauseMenu = () => {
    const { audioVolume, setAudioVolume, audioEnabled, toggleAudio, theme, setTheme, isPaused, setPaused } = useStore();
    
    // Graphics settings (local state for now, can persist to store later)
    const [dprScale, setDprScale] = useState(1);
    const [bloomIntensity, setBloomIntensity] = useState(1);
    const [particleCount, setParticleCount] = useState<'low' | 'medium' | 'high'>('high');
    const [drawDistance, setDrawDistance] = useState(200);
    
    // Controls settings
    const [cameraSensitivity, setCameraSensitivity] = useState(1);
    const [invertYAxis, setInvertYAxis] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setPaused(!isPaused);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isPaused, setPaused]);

    if (!isPaused) return null;

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5000,
            pointerEvents: 'auto'
        }}>
            <div style={{
                backgroundColor: '#1a1a20',
                border: '2px solid #555',
                borderRadius: '12px',
                padding: '40px',
                maxWidth: '500px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.9)',
                fontFamily: "'Inter', sans-serif",
                maxHeight: '90vh',
                overflowY: 'auto'
            }}>
                {/* Title */}
                <h1 style={{
                    margin: '0 0 30px 0',
                    color: '#e2e2e2',
                    fontSize: '32px',
                    letterSpacing: '2px',
                    textAlign: 'center'
                }}>PAUSED</h1>

                {/* Volume Control */}
                <div style={{ marginBottom: '24px' }}>
                    <label style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#ccc',
                        marginBottom: '8px',
                        fontSize: '14px'
                    }}>
                        <span>Master Volume</span>
                        <span style={{ color: '#888' }}>{Math.round(audioVolume * 100)}%</span>
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={audioVolume}
                        onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
                        style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: '#333',
                            outline: 'none',
                            cursor: 'pointer',
                            accentColor: '#646cff'
                        }}
                    />
                </div>

                {/* Audio Toggle */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px',
                    padding: '12px',
                    backgroundColor: '#252530',
                    borderRadius: '8px',
                    cursor: 'pointer'
                }}
                    onClick={toggleAudio}
                >
                    <input
                        type="checkbox"
                        checked={audioEnabled}
                        onChange={toggleAudio}
                        style={{ cursor: 'pointer' }}
                    />
                    <label style={{ color: '#ccc', cursor: 'pointer', flex: 1 }}>
                        Audio Enabled
                    </label>
                </div>

                {/* Theme Toggle */}
                <div style={{ marginBottom: '24px' }}>
                    <label style={{
                        display: 'block',
                        color: '#ccc',
                        marginBottom: '8px',
                        fontSize: '14px'
                    }}>Theme</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {['medieval', 'cyberpunk'].map((t) => (
                            <button
                                key={t}
                                onClick={() => setTheme(t as 'medieval' | 'cyberpunk')}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    backgroundColor: theme === t ? '#646cff' : '#252530',
                                    color: theme === t ? '#fff' : '#ccc',
                                    border: '1px solid ' + (theme === t ? '#646cff' : '#3a3a45'),
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {t === 'medieval' ? '⚔️ Medieval' : '🌐 Cyberpunk'}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', backgroundColor: '#333', margin: '24px 0' }} />

                {/* GRAPHICS SECTION */}
                <h3 style={{
                    color: '#e2e2e2',
                    fontSize: '14px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '16px',
                    marginTop: '0'
                }}>Graphics</h3>

                {/* DPR Scale */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        color: '#ccc',
                        marginBottom: '6px',
                        fontSize: '12px'
                    }}>
                        <span>Resolution Scale (DPR)</span>
                        <span style={{ color: '#888' }}>{(dprScale * 100).toFixed(0)}%</span>
                    </label>
                    <input
                        type="range"
                        min="0.5"
                        max="1"
                        step="0.1"
                        value={dprScale}
                        onChange={(e) => setDprScale(parseFloat(e.target.value))}
                        style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: '#333',
                            outline: 'none',
                            cursor: 'pointer',
                            accentColor: '#646cff'
                        }}
                    />
                    <p style={{ fontSize: '10px', color: '#666', margin: '4px 0 0 0' }}>Lower = faster, less detail</p>
                </div>

                {/* Bloom Intensity */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        color: '#ccc',
                        marginBottom: '6px',
                        fontSize: '12px'
                    }}>
                        <span>Bloom Intensity</span>
                        <span style={{ color: '#888' }}>{bloomIntensity.toFixed(1)}</span>
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={bloomIntensity}
                        onChange={(e) => setBloomIntensity(parseFloat(e.target.value))}
                        style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: '#333',
                            outline: 'none',
                            cursor: 'pointer',
                            accentColor: '#646cff'
                        }}
                    />
                </div>

                {/* Particle Count */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        color: '#ccc',
                        marginBottom: '6px',
                        fontSize: '12px'
                    }}>Particle Count</label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        {['low', 'medium', 'high'].map((level) => (
                            <button
                                key={level}
                                onClick={() => setParticleCount(level as 'low' | 'medium' | 'high')}
                                style={{
                                    flex: 1,
                                    padding: '8px',
                                    backgroundColor: particleCount === level ? '#646cff' : '#252530',
                                    color: particleCount === level ? '#fff' : '#ccc',
                                    border: '1px solid ' + (particleCount === level ? '#646cff' : '#3a3a45'),
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {level}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Draw Distance */}
                <div style={{ marginBottom: '24px' }}>
                    <label style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        color: '#ccc',
                        marginBottom: '6px',
                        fontSize: '12px'
                    }}>
                        <span>Draw Distance</span>
                        <span style={{ color: '#888' }}>{drawDistance}m</span>
                    </label>
                    <input
                        type="range"
                        min="50"
                        max="300"
                        step="10"
                        value={drawDistance}
                        onChange={(e) => setDrawDistance(parseInt(e.target.value))}
                        style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: '#333',
                            outline: 'none',
                            cursor: 'pointer',
                            accentColor: '#646cff'
                        }}
                    />
                </div>

                {/* Divider */}
                <div style={{ height: '1px', backgroundColor: '#333', margin: '24px 0' }} />

                {/* CONTROLS SECTION */}
                <h3 style={{
                    color: '#e2e2e2',
                    fontSize: '14px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    marginBottom: '16px',
                    marginTop: '0'
                }}>Controls</h3>

                {/* Camera Sensitivity */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        color: '#ccc',
                        marginBottom: '6px',
                        fontSize: '12px'
                    }}>
                        <span>Camera Sensitivity</span>
                        <span style={{ color: '#888' }}>{(cameraSensitivity * 100).toFixed(0)}%</span>
                    </label>
                    <input
                        type="range"
                        min="0.5"
                        max="2"
                        step="0.1"
                        value={cameraSensitivity}
                        onChange={(e) => setCameraSensitivity(parseFloat(e.target.value))}
                        style={{
                            width: '100%',
                            height: '6px',
                            borderRadius: '3px',
                            background: '#333',
                            outline: 'none',
                            cursor: 'pointer',
                            accentColor: '#646cff'
                        }}
                    />
                </div>

                {/* Invert Y Axis */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px',
                    padding: '12px',
                    backgroundColor: '#252530',
                    borderRadius: '8px',
                    cursor: 'pointer'
                }}
                    onClick={() => setInvertYAxis(!invertYAxis)}
                >
                    <input
                        type="checkbox"
                        checked={invertYAxis}
                        onChange={() => setInvertYAxis(!invertYAxis)}
                        style={{ cursor: 'pointer' }}
                    />
                    <label style={{ color: '#ccc', cursor: 'pointer', flex: 1, fontSize: '12px' }}>
                        Invert Y Axis
                    </label>
                </div>

                {/* Divider */}
                <div style={{ height: '1px', backgroundColor: '#333', margin: '24px 0' }} />

                {/* Button Group */}
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'space-between'
                }}>
                    <button
                        onClick={() => setPaused(false)}
                        style={{
                            flex: 1,
                            padding: '12px',
                            backgroundColor: '#646cff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#7b83ff';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#646cff';
                        }}
                    >
                        Resume (Esc)
                    </button>
                    <button
                        onClick={() => window.location.href = '/'}
                        style={{
                            flex: 1,
                            padding: '12px',
                            backgroundColor: '#555',
                            color: '#ccc',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#666';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#555';
                        }}
                    >
                        Main Menu
                    </button>
                </div>

                {/* Hint */}
                <p style={{
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '11px',
                    marginTop: '16px',
                    margin: '16px 0 0 0'
                }}>Press ESC to resume or click Resume button</p>
            </div>
        </div>
    );
};
